/**
 * Zod validation schemas for API inputs.
 * Used across CMS API routes for request validation.
 */
import { z } from 'zod';
import { Orientation, ScreenStatus } from './types';
import { healthComponentSchema } from './recycle-contract';
// ─── ScreenGroup Validation ─────────────────────────────────────────────────
export const createScreenGroupSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    parentId: z.string().cuid().optional().nullable(),
});
export const updateScreenGroupSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    parentId: z.string().cuid().optional().nullable(),
});
// ─── Screen Validation ──────────────────────────────────────────────────────
/**
 * Canonical Raspberry Pi device ID. This strict format is a safe subset of
 * the MQTT topic component charset and therefore also prevents topic injection.
 */
const DEVICE_ID_REGEX = /^TS_[0-9A-F]{8}$/;
const DEVICE_ID_MESSAGE = 'Device ID must use the format TS_ followed by eight uppercase hexadecimal characters';
/**
 * Install-survey fields shared by both device classes (Screen and
 * CounterDevice) so a single registration/approval UI can drive either one.
 */
const locationField = z.string().max(255, 'Location must be at most 255 characters');
const areaField = z.string().max(255, 'Area must be at most 255 characters');
const installPhotoKeyField = z
    .string()
    .max(1024, 'Install photo key must be at most 1024 characters');
const latitudeField = z.number().min(-90).max(90);
const longitudeField = z.number().min(-180).max(180);
const resolutionField = z
    .string()
    .max(50)
    .regex(/^\d+x\d+$/, 'Resolution must be in WxH format (e.g., 1920x1080)');
export const createScreenSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    deviceId: z
        .string()
        .min(1, 'Device ID is required')
        .max(255, 'Device ID must be at most 255 characters')
        .regex(DEVICE_ID_REGEX, DEVICE_ID_MESSAGE),
    screenGroupId: z.string().cuid().optional().nullable(),
    timezone: z.string().max(255).optional(),
    resolution: resolutionField.optional(),
    orientation: z.nativeEnum(Orientation).optional(),
    latitude: latitudeField.optional().nullable(),
    longitude: longitudeField.optional().nullable(),
    address: z.string().max(500).optional().nullable(),
    location: locationField.optional().nullable(),
    area: areaField.optional().nullable(),
    installPhotoKey: installPhotoKeyField.optional().nullable(),
});
// ─── Ticker (#212) ───────────────────────────────────────────────────────────
/** Scroll style for the player's bottom ticker strip. */
export const TICKER_DIRECTIONS = ['rtl', 'ltr', 'up', 'down'];
/** Font families available on the kiosk (web-safe; no runtime font fetch). */
export const TICKER_FONTS = ['sans', 'serif', 'mono', 'condensed', 'black', 'impact'];
/**
 * Per-screen ticker configuration (#212). Stored on Screen.tickerConfig and
 * delivered to the player inside the schedule manifest. The strip is a fixed
 * 30px section at the bottom of the screen for standing messages like
 * "SCAN ITEM TO RECYCLE".
 */
export const tickerConfigSchema = z.object({
    enabled: z.boolean(),
    text: z.string().min(1).max(500),
    /** CSS hex color, e.g. #16a34a */
    bgColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color'),
    textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color'),
    font: z.enum(TICKER_FONTS),
    /** rtl/ltr marquee, or up/down vertical crawl through the strip */
    direction: z.enum(TICKER_DIRECTIONS),
    /** Scroll speed in px/s (marquee) or s/loop-ish equivalent for vertical. */
    speed: z.number().int().min(10).max(300).default(60),
    /** Strip height in px. */
    height: z.number().int().min(30).max(200).default(90),
});
// ─── Sleep mode ──────────────────────────────────────────────────────────────
const sleepTimeField = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM (24h)');
/**
 * Scheduled sleep window. Stored on Screen.sleepConfig and compiled into the
 * schedule manifest as `sleep`. While the window is active the player
 * suspends playback, dims to `brightness`, and shows the sleep overlay.
 * Midnight-crossing windows (e.g. 22:00–06:30) are supported.
 */
export const sleepWindowSchema = z.object({
    startTime: sleepTimeField,
    endTime: sleepTimeField,
    /** ISO days of week (1=Monday … 7=Sunday). */
    daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1),
    /** Overlay animation variant (defaults to 'night-drift' player-side). */
    variant: z.enum(['night-drift', 'face']).optional(),
    /** Backlight brightness while asleep, 0–1 (default 0.15 player-side). */
    brightness: z.number().min(0).max(1).optional(),
});
export const updateScreenSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    screenGroupId: z.string().cuid().optional().nullable(),
    timezone: z.string().max(255).optional(),
    resolution: resolutionField.optional(),
    orientation: z.nativeEnum(Orientation).optional(),
    latitude: latitudeField.optional().nullable(),
    longitude: longitudeField.optional().nullable(),
    address: z.string().max(500).optional().nullable(),
    location: locationField.optional().nullable(),
    area: areaField.optional().nullable(),
    installPhotoKey: installPhotoKeyField.optional().nullable(),
    status: z.nativeEnum(ScreenStatus).optional(),
    /** Scheduled sleep window; null clears it (screen inherits the fleet sleep
     * day part, if any). Accepts a partial window so variant/brightness can be
     * set while the window itself comes from sleepDayPartId — the compiler only
     * emits a manifest sleep window when it can resolve complete start/end/
     * days. `{ disabled: true }` is the explicit opt-out: never sleep, even
     * when a fleet sleep day part exists. */
    sleepConfig: sleepWindowSchema
        .partial()
        .extend({ disabled: z.boolean().optional() })
        .optional()
        .nullable(),
    /** DayPart supplying the sleep window; null clears it. Wins over the
     * window fields in sleepConfig when both are set. */
    sleepDayPartId: z.string().cuid().optional().nullable(),
});
// ─── Pairing Validation ─────────────────────────────────────────────────────
export const initiatePairingSchema = z.object({
    deviceId: z
        .string()
        .min(1, 'Device ID is required')
        .max(255, 'Device ID must be at most 255 characters')
        .regex(DEVICE_ID_REGEX, DEVICE_ID_MESSAGE),
});
export const completePairingSchema = z.object({
    deviceCode: z
        .string()
        .min(1, 'Device code is required'),
});
export const confirmPairingSchema = z.object({
    userCode: z
        .string()
        .min(1, 'User code is required')
        .max(10, 'User code must be at most 10 characters'),
    screenName: z
        .string()
        .min(1, 'Screen name is required')
        .max(255, 'Screen name must be at most 255 characters'),
    screenGroupId: z.string().cuid().optional().nullable(),
    timezone: z.string().max(255).optional(),
    resolution: resolutionField.optional(),
    orientation: z.nativeEnum(Orientation).optional(),
});
// ─── DayPart Validation ──────────────────────────────────────────────────────
const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const createDayPartSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    startTime: z
        .string()
        .regex(timeRegex, 'Start time must be in HH:mm format (00:00-23:59)'),
    endTime: z
        .string()
        .regex(timeRegex, 'End time must be in HH:mm format (00:00-23:59)'),
    daysOfWeek: z
        .array(z.number().int().min(1, 'Day must be 1-7 (Mon-Sun)').max(7, 'Day must be 1-7 (Mon-Sun)'))
        .min(1, 'At least one day is required')
        .default([1, 2, 3, 4, 5, 6, 7]),
    /** Marks this day part as the fleet-wide sleep window: every screen without
     * its own sleep settings sleeps during it. */
    isSleep: z.boolean().optional(),
}).refine(
// Sleep windows are allowed to cross midnight (22:00–06:30) — the player
// sleep evaluator handles wrapping, same as schedule slots.
(data) => data.isSleep || data.startTime < data.endTime, { message: 'Start time must be before end time', path: ['startTime'] });
export const updateDayPartSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    startTime: z
        .string()
        .regex(timeRegex, 'Start time must be in HH:mm format (00:00-23:59)')
        .optional(),
    endTime: z
        .string()
        .regex(timeRegex, 'End time must be in HH:mm format (00:00-23:59)')
        .optional(),
    daysOfWeek: z
        .array(z.number().int().min(1, 'Day must be 1-7 (Mon-Sun)').max(7, 'Day must be 1-7 (Mon-Sun)'))
        .min(1, 'At least one day is required')
        .optional(),
    isSleep: z.boolean().optional(),
}).refine((data) => {
    // Only validate time ordering if both are provided (and not a sleep window)
    if (data.isSleep)
        return true;
    if (data.startTime !== undefined && data.endTime !== undefined) {
        return data.startTime < data.endTime;
    }
    return true;
}, { message: 'Start time must be before end time', path: ['startTime'] });
// ─── Playlist Validation ────────────────────────────────────────────────────
export const createPlaylistSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    isDefault: z.boolean().optional(),
});
export const updatePlaylistSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    isDefault: z.boolean().optional(),
    shuffle: z.boolean().optional(),
    // #212: bottom ticker shown while this playlist's content plays;
    // explicit null clears it
    tickerConfig: tickerConfigSchema.optional().nullable(),
});
export const addPlaylistItemSchema = z.object({
    assetId: z.string().cuid('Invalid asset ID'),
    duration: z
        .number()
        .int('Duration must be an integer')
        .min(1, 'Duration must be at least 1 second')
        .max(86400, 'Duration must be at most 86400 seconds (24h)')
        .optional()
        .nullable(),
    order: z
        .number()
        .int('Order must be an integer')
        .min(0, 'Order must be non-negative')
        .optional(),
});
export const updatePlaylistItemSchema = z.object({
    duration: z
        .number()
        .int('Duration must be an integer')
        .min(1, 'Duration must be at least 1 second')
        .max(86400, 'Duration must be at most 86400 seconds (24h)')
        .optional()
        .nullable(),
    order: z
        .number()
        .int('Order must be an integer')
        .min(0, 'Order must be non-negative')
        .optional(),
});
export const reorderPlaylistItemsSchema = z
    .object({
    items: z.array(z.object({
        id: z.string().cuid('Invalid item ID'),
        order: z.number().int().min(0, 'Order must be non-negative'),
    })).min(1, 'At least one item is required'),
})
    .superRefine((data, ctx) => {
    // Reject payloads with duplicate ids or duplicate order values. The
    // (playlistId, order) unique constraint would reject duplicate orders at
    // COMMIT, but surfacing it here returns a clean 400 instead of a 409/500.
    const ids = new Set();
    const orders = new Set();
    for (const item of data.items) {
        if (ids.has(item.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['items'],
                message: 'Duplicate item id in reorder payload',
            });
        }
        if (orders.has(item.order)) {
            ctx.addIssue({
                code: 'custom',
                path: ['items'],
                message: 'Duplicate order value in reorder payload',
            });
        }
        ids.add(item.id);
        orders.add(item.order);
    }
});
// ─── Campaign Validation ────────────────────────────────────────────────────
import { CampaignStatus } from './types';
const timeRegexCampaign = /^([01]\d|2[0-3]):([0-5]\d)$/;
/**
 * Schema for a single campaign schedule entry.
 * Each entry defines a time window and optional day-of-week mask / daypart reference.
 */
export const campaignScheduleSchema = z.object({
    startTime: z
        .string()
        .regex(timeRegexCampaign, 'Start time must be in HH:mm format (00:00-23:59)'),
    endTime: z
        .string()
        .regex(timeRegexCampaign, 'End time must be in HH:mm format (00:00-23:59)'),
    daysOfWeek: z
        .array(z.number().int().min(1).max(7))
        .min(1, 'At least one day is required')
        .default([1, 2, 3, 4, 5, 6, 7]),
    dayPartId: z.string().cuid().optional().nullable(),
    /** Sleep day part: screens sleep during this window instead of playing. */
    isSleep: z.boolean().optional(),
    /** Playlist this slot plays; null = the campaign's playlists merged. */
    playlistId: z.string().cuid().optional().nullable(),
}).refine(
// Sleep day parts are allowed to cross midnight (22:00–06:00) — overnight is
// the common case, and the player's sleep evaluator handles wrapping.
(data) => data.isSleep || data.startTime < data.endTime, { message: 'Start time must be before end time', path: ['startTime'] });
export const createCampaignSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    priority: z
        .number()
        .int('Priority must be an integer')
        .min(0, 'Priority must be non-negative')
        .max(10000, 'Priority must be at most 10000')
        .default(0),
    startDate: z
        .string()
        .min(1, 'Start date is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid start date' }),
    endDate: z
        .string()
        .min(1, 'End date is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid end date' }),
    screenIds: z
        .array(z.string().cuid('Invalid screen ID'))
        .default([]),
    screenGroupIds: z
        .array(z.string().cuid('Invalid screen group ID'))
        .optional(),
    playlistIds: z
        .array(z.string().cuid('Invalid playlist ID'))
        .default([]),
    schedules: z
        .array(campaignScheduleSchema)
        .default([]),
    /** Overlay animation for this campaign's sleep day parts. */
    sleepVariant: z.enum(['night-drift', 'face']).optional().nullable(),
    /** Backlight brightness while asleep, 0-1. */
    sleepBrightness: z.number().min(0).max(1).optional().nullable(),
    status: z
        .nativeEnum(CampaignStatus)
        .optional(),
}).refine((data) => new Date(data.startDate) < new Date(data.endDate), { message: 'Start date must be before end date', path: ['startDate'] }).refine((data) => data.screenIds.length + (data.screenGroupIds?.length ?? 0) > 0, { message: 'At least one screen or screen group is required', path: ['screenIds'] });
export const updateCampaignSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    priority: z
        .number()
        .int('Priority must be an integer')
        .min(0, 'Priority must be non-negative')
        .max(10000, 'Priority must be at most 10000')
        .optional(),
    startDate: z
        .string()
        .min(1, 'Start date is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid start date' })
        .optional(),
    endDate: z
        .string()
        .min(1, 'End date is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid end date' })
        .optional(),
    screenIds: z
        .array(z.string().cuid('Invalid screen ID'))
        .optional(),
    screenGroupIds: z
        .array(z.string().cuid('Invalid screen group ID'))
        .optional(),
    playlistIds: z
        .array(z.string().cuid('Invalid playlist ID'))
        .optional(),
    schedules: z
        .array(campaignScheduleSchema)
        .optional(),
    /** Overlay animation for this campaign's sleep day parts. */
    sleepVariant: z.enum(['night-drift', 'face']).optional().nullable(),
    /** Backlight brightness while asleep, 0-1. */
    sleepBrightness: z.number().min(0).max(1).optional().nullable(),
    status: z
        .nativeEnum(CampaignStatus)
        .optional(),
}).refine((data) => {
    if (data.startDate !== undefined && data.endDate !== undefined) {
        return new Date(data.startDate) < new Date(data.endDate);
    }
    return true;
}, { message: 'Start date must be before end date', path: ['startDate'] }).refine((data) => {
    if (data.screenIds !== undefined && data.screenGroupIds !== undefined) {
        return data.screenIds.length + data.screenGroupIds.length > 0;
    }
    return true;
}, { message: 'At least one screen or screen group is required', path: ['screenIds'] });
// ─── Heartbeat Validation ───────────────────────────────────────────────────
export const heartbeatInputSchema = z.object({
    deviceId: z
        .string()
        .min(1, 'Device ID is required')
        .max(255, 'Device ID must be at most 255 characters'),
    timestamp: z
        .string()
        .min(1, 'Timestamp is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid timestamp' }),
    status: z
        .enum(['online', 'offline'])
        .default('online'),
    currentAssetId: z
        .string()
        .nullable()
        .optional(),
    diskUsagePercent: z
        .number()
        .min(0)
        .max(100)
        .optional(),
    cpuTempC: z
        .number()
        .min(-40)
        .max(120)
        .optional(),
    networkLatencyMs: z
        .number()
        .min(0)
        .optional(),
    scheduleVersion: z
        .string()
        .optional(),
    recycle: z
        .object({
        agent: z.enum(['up', 'down', 'absent']),
        // Optional: when agent === 'absent' (never connected) or 'down'
        // (dropped before a health snapshot arrived), the player has no
        // component readings to report yet.
        servo: healthComponentSchema.optional(),
        dropSensor: healthComponentSchema.optional(),
        // @deprecated NFC hardware was removed from the agent runtime (v2).
        // Kept optional for wire compatibility with older heartbeat payloads
        // that still send it — never populated by the current agent/player.
        nfc: healthComponentSchema.optional(),
        binFillPercent: z.number().min(0).max(100).nullable().optional(),
        // v2 additive: agent operational state (e.g. "IDLE", "OUT_OF_SERVICE",
        // "OBSTRUCTED", "RESTART_REQUIRED") mirrored from
        // agentStatusHealthSchema.state (recycle-contract.ts) so CMS fleet
        // health can flag a kiosk that needs attention.
        state: z.string().optional(),
        // v2 additive: present only while state === "OUT_OF_SERVICE" — mirrors
        // agentStatusHealthSchema.outOfServiceReason.
        outOfServiceReason: z.enum(['bin_full', 'hardware_fault']).optional(),
        // v2 additive: agent-side barcode catalog cache version and offline
        // verdict outbox depth. Not yet produced by the agent — schema shape
        // defined ahead of that wiring so the heartbeat contract is stable;
        // fields pass through opportunistically once populated.
        catalogVersion: z.number().optional(),
        outboxDepth: z.number().optional(),
    })
        .optional(),
});
// ─── Interaction (recycle) Validation ────────────────────────────────────────
export const interactionOutcomeSchema = z.enum([
    'SUCCESS', 'NO_ITEM', 'NO_MATCH', 'QR_REJECT', 'OBSTRUCTED',
]);
export const interactionEntrySchema = z.object({
    txnId: z.string().min(1, 'txnId is required').max(128),
    barcode: z.string().max(256).nullable().optional().transform((v) => v ?? null),
    outcome: interactionOutcomeSchema,
    at: z
        .string()
        .min(1, 'Timestamp is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid timestamp' }),
});
export const interactionBatchSchema = z.object({
    deviceId: z.string().min(1, 'Device ID is required').max(255),
    screenId: z.string().min(1, 'Screen ID is required'),
    interactions: z
        .array(interactionEntrySchema)
        .min(1, 'At least one interaction is required')
        .max(200, 'At most 200 interactions per batch'),
});
// ─── PlayLog Validation ─────────────────────────────────────────────────────
export const playLogEntrySchema = z.object({
    assetId: z
        .string()
        .min(1, 'Asset ID is required'),
    campaignId: z
        .string()
        .nullable()
        .optional(),
    startedAt: z
        .string()
        .min(1, 'Start timestamp is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid start timestamp' }),
    endedAt: z
        .string()
        .min(1, 'End timestamp is required')
        .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid end timestamp' }),
    duration: z
        .number()
        .int('Duration must be an integer')
        .min(0, 'Duration must be non-negative')
        .max(86400, 'Duration must be at most 86400 seconds (24h)'),
    interrupted: z.literal('INTERACTION').optional(),
});
export const playLogBatchSchema = z.object({
    deviceId: z
        .string()
        .min(1, 'Device ID is required')
        .max(255, 'Device ID must be at most 255 characters'),
    screenId: z
        .string()
        .min(1, 'Screen ID is required'),
    logs: z
        .array(playLogEntrySchema)
        .min(1, 'At least one play log entry is required')
        .max(500, 'At most 500 play log entries per batch'),
});
// ─── CounterDevice Validation (#220) ────────────────────────────────────────
/** AWS IoT thing name of a people-counter sensor, e.g. DC_6E7788E8. */
const counterThingNameRegex = /^[A-Za-z0-9_-]{1,64}$/;
/** Validate an IANA timezone by probing the Intl API (throws on unknown). */
function isValidTimezone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
export const createCounterDeviceSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters'),
    thingName: z
        .string()
        .regex(counterThingNameRegex, 'Thing name must be 1-64 chars of letters, digits, _ or -'),
    timezone: z
        .string()
        .refine(isValidTimezone, 'Timezone must be a valid IANA identifier')
        .default('America/New_York'),
    address: z
        .string()
        .max(500, 'Address must be at most 500 characters')
        .optional(),
    location: locationField.optional().nullable(),
    area: areaField.optional().nullable(),
    installPhotoKey: installPhotoKeyField.optional().nullable(),
    latitude: latitudeField.optional().nullable(),
    longitude: longitudeField.optional().nullable(),
});
export const updateCounterDeviceSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be at most 255 characters')
        .optional(),
    timezone: z
        .string()
        .refine(isValidTimezone, 'Timezone must be a valid IANA identifier')
        .optional(),
    address: z
        .string()
        .max(500, 'Address must be at most 500 characters')
        .nullable()
        .optional(),
    location: locationField.optional().nullable(),
    area: areaField.optional().nullable(),
    installPhotoKey: installPhotoKeyField.optional().nullable(),
    latitude: latitudeField.optional().nullable(),
    longitude: longitudeField.optional().nullable(),
});
// ─── Device Self-Registration ───────────────────────────────────────────────
/** TS_ is a signage screen, VS_ a people-counter sensor. */
const DEVICE_IDENTIFIER_REGEX = /^(TS|VS)_/;
/**
 * Payload a freshly imaged device POSTs to the PUBLIC (unauthenticated)
 * registration endpoint, so this schema is the trust boundary: every string
 * carries an explicit max length to keep an anonymous caller from pushing
 * unbounded data into the pending-registration table.
 */
export const deviceRegistrationRequestSchema = z.object({
    deviceIdentifier: z
        .string()
        .min(1, 'Device identifier is required')
        .max(255, 'Device identifier must be at most 255 characters')
        .regex(DEVICE_IDENTIFIER_REGEX, 'Device identifier must start with TS_ or VS_'),
    firmwareVersion: z
        .string()
        .max(64, 'Firmware version must be at most 64 characters')
        .optional(),
    location: locationField.optional(),
    area: areaField.optional(),
    latitude: latitudeField.optional().nullable(),
    longitude: longitudeField.optional().nullable(),
    timezone: z
        .string()
        .refine(isValidTimezone, 'Timezone must be a valid IANA identifier')
        .optional(),
    resolution: resolutionField.optional(),
    orientation: z.nativeEnum(Orientation).optional(),
});
//# sourceMappingURL=validation.js.map